import * as React from 'react'
import { Dialog, DialogActions, DialogContent, Paper, Typography, IconButton } from '@mui/material'
// It must be imported on project that installed this package
// import 'swiper/swiper.scss'
// import 'swiper/components/navigation/navigation.scss'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Keyboard } from 'swiper'
import { styled, useTheme } from '../../theme/styled'
import { CloseIcon } from '../icons/CloseIcon'
import { ChevronIcon } from '../icons/ChevronIcon'
import { useEffect, useState } from 'react'
import { PhotoGalleryProps, GalleryPhoto } from './PhotoGalleryProps'

SwiperCore.use([Navigation, Keyboard])

const GalleryDialog = styled(Dialog)`
  & > .MuiBackdrop-root {
    background: ${(p) => p.theme.av8.gallery.background};
  }
`

const GalleryPaper = styled(Paper)`
  position: relative;
  padding: 2rem;
  overflow: visible !important;
  max-width: 60vw !important;
`

const GalleryDialogTitle = styled(Typography)<{ $hasTitle: boolean }>`
  justify-content: space-between;
  align-items: flex-start;
  padding: 0;
  padding-bottom: 17px;
  display: flex;
  flex-direction: ${(props) => (props.$hasTitle ? 'row' : 'row-reverse')};
`

const CloseButton = styled(IconButton)`
  position: relative;
  padding: 0.5rem;
  z-index: 10;
  left: 0.5rem;
`

const NavButton = styled(IconButton)`
  border: 1px solid ${(p) => p.theme.av8.input.borderColor};
  width: 48px;
  height: 48px;
`

type PhotoGalleryDialogContentProps = {
  $hasTitle: boolean
  $hasActions: boolean
}
const PhotoGalleryDialogContent = styled(DialogContent)<PhotoGalleryDialogContentProps>`
  padding: 0;
  display: block;
  height: 60vh;

  & .swiper-container {
    width: 100%;
    max-width: 100%;
    height: 100%;
  }

  & .swiper-slide {
    width: auto;
    flex-shrink: 0;
    display: block;
    height: 100%;
    max-height: 100%;
  }

  & .swiper-wrapper {
    max-height: 100%;
    height: 100%;
    display: flex;
  }

  & .nextElement,
  & .prevElement {
    position: absolute;
    z-index: 1;
    position: absolute;
    top: calc(50% - (48px / 2));
  }

  & .nextElement {
    right: -80px;
  }

  & .prevElement {
    left: -80px;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    border-radius: 4px;
  }
`

const PhotoGalleryDialogActions = styled(DialogActions)`
  padding: 0;
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`

const ActionButton = styled(Typography)`
  display: flex;
  cursor: pointer;
  align-items: center;
  text-decoration: underline;
  font-size: 16px;
  padding: 0.25rem 0;

  & > svg {
    padding: 0 0.5rem;
  }
`

const PhotoGallery = ({
  title,
  photos,
  actions,
  initialIndex = 0,
  fit,
  contentProps,
  swiperProps,
  ...props
}: PhotoGalleryProps) => {
  const [currentSlide, setCurrentSlide] = useState<GalleryPhoto>(photos[initialIndex])
  const theme = useTheme()

  // Set the overflow manually to avoid providing incorrect width
  // for the Swiper element. If the overflow changes is made by MUI,
  // Swiper might calculate the width before scrollbar is removed.
  useEffect(() => {
    if (!props.shouldSetOverflowManually) return

    if (props.open) {
      document.getElementsByTagName('body')[0].style.overflow = 'hidden'
    } else {
      document.getElementsByTagName('body')[0].style.overflow = 'auto'
    }
  }, [props.open, props.shouldSetOverflowManually])

  if (photos.length === 0) {
    throw Error('Photo Gallery must have at least one image')
  }

  return (
    <GalleryDialog PaperComponent={GalleryPaper} style={{ zIndex: 2000 }} {...props}>
      <GalleryDialogTitle $hasTitle={!!title}>
        {title ? <Typography variant='h4'>{title}</Typography> : null}
        <CloseButton onClick={props.onClose}>
          <CloseIcon color={theme.av8.primaryColor} />
        </CloseButton>
      </GalleryDialogTitle>

      <PhotoGalleryDialogContent {...contentProps} $hasTitle={!!title} $hasActions={!!actions}>
        <NavButton className='prevElement'>
          <ChevronIcon rotate={180} color={theme.av8.primaryColor} />
        </NavButton>
        <NavButton className='nextElement'>
          <ChevronIcon color={theme.av8.primaryColor} />
        </NavButton>
        <Swiper
          key={photos.length}
          centeredSlides
          loop
          mousewheel
          pagination={false}
          slidesPerView={1}
          navigation={{
            nextEl: '.nextElement',
            prevEl: '.prevElement'
          }}
          initialSlide={initialIndex}
          onSnapIndexChange={(swiper) => {
            const activeSlide = swiper.realIndex
            setCurrentSlide(photos[activeSlide])
          }}
          keyboard
          centerInsufficientSlides={true}
          {...swiperProps}
        >
          {photos.map(({ src, alt }, index) => {
            return (
              <SwiperSlide key={`${alt}-${index}`}>
                <ImageContainer>
                  <img src={src} alt={alt} style={{ objectFit: fit }} />
                </ImageContainer>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </PhotoGalleryDialogContent>

      {actions ? (
        <PhotoGalleryDialogActions>
          {actions.map(({ text, icon, onClick }, index) => (
            <ActionButton
              variant='body2'
              key={index}
              onClick={() => onClick(currentSlide)}
              role={'button'}
            >
              {icon ? icon() : null}

              <span>{text}</span>
            </ActionButton>
          ))}
        </PhotoGalleryDialogActions>
      ) : null}
    </GalleryDialog>
  )
}

export default PhotoGallery
